import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Card, Table, Typography, Button, Space, Row, Col } from "antd";
import { DownloadOutlined, FilterOutlined } from "@ant-design/icons";
import Select from "react-select";
import * as XLSX from "xlsx";
import "./styles.css";

const { Title, Text } = Typography;

const TotalDoseConsumptionDistrictWise = ({ data = [] }) => {
  // State for filters - changed to array for multi-select
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedTreatmentCenter, setSelectedTreatmentCenter] = useState("all");

  // Process and group data by product_code and treatment_center_id
  const processedData = useMemo(() => {
    const groupedData = {};

    data.forEach((item) => {
      const productCode = item.product_code;
      const tcId = item.s_register?.tc_id?._id || "unknown";
      const tcName = item.tc_name || "Unknown";
      const district = item.s_register?.tc_id?.tcenters?.district || "Unknown";
      const doseGiven = item.s_register?.dose_given || 0;
      const quantity = item.s_register?.quantity || 0;

      // Create key for grouping
      const key = `${productCode}-${tcId}`;

      if (!groupedData[key]) {
        groupedData[key] = {
          product_code: productCode,
          product_name: item.product_name,
          tc_id: tcId,
          tc_name: tcName,
          district: district,
          total_dose_given: 0,
          total_quantity: 0,
        };
      }

      groupedData[key].total_dose_given += doseGiven;
      groupedData[key].total_quantity += quantity;
    });

    return Object.values(groupedData);
  }, [data]);

  // Prepare data for chart visualization
  const chartData = useMemo(() => {
    const districtSummary = {};

    processedData.forEach((item) => {
      if (!districtSummary[item.district]) {
        districtSummary[item.district] = {};
      }

      if (!districtSummary[item.district][item.product_code]) {
        districtSummary[item.district][item.product_code] = 0;
      }

      districtSummary[item.district][item.product_code] +=
        item.total_dose_given;
    });

    return Object.keys(districtSummary).map((district) => {
      const districtData = {
        district: district,
      };

      // Add product consumption to district data
      Object.keys(districtSummary[district]).forEach((productCode) => {
        districtData[productCode] = districtSummary[district][productCode];
      });

      return districtData;
    });
  }, [processedData]);

  // Get unique product codes for chart and filter dropdown
  const uniqueProductCodes = useMemo(() => {
    const productCodes = new Set();
    processedData.forEach((item) => productCodes.add(item.product_code));
    return Array.from(productCodes);
  }, [processedData]);

  // Calculate date interval (oldest to newest date)
  const dateInterval = useMemo(() => {
    // Check if data exists and has items
    if (!data || data.length === 0) {
      return {
        oldest: "N/A",
        newest: "N/A",
        display: "No date range available",
      };
    }

    // Initialize with extreme values in opposite directions to ensure proper comparison
    let oldestDate = null;
    let newestDate = null;
    let hasValidDates = false;

    // Find the oldest and newest dates
    data.forEach((item) => {
      // Check for visit_date in the item or its s_register
      const itemDate = item.visit_date
        ? new Date(item.visit_date)
        : item.s_register?.visit_date
        ? new Date(item.s_register.visit_date)
        : null;

      if (itemDate && !isNaN(itemDate.getTime())) {
        // Ensure it's a valid date
        hasValidDates = true;

        // Initialize dates on first valid date found
        if (oldestDate === null) oldestDate = itemDate;
        if (newestDate === null) newestDate = itemDate;

        // Compare and update
        if (itemDate < oldestDate) oldestDate = itemDate;
        if (itemDate > newestDate) newestDate = itemDate;
      }
    });

    // If no valid dates were found
    if (!hasValidDates) {
      return {
        oldest: "N/A",
        newest: "N/A",
        display: "No valid dates found",
      };
    }

    // Format dates
    const formatDate = (date) => {
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    return {
      oldest: formatDate(oldestDate),
      newest: formatDate(newestDate),
      display: `${formatDate(oldestDate)} - ${formatDate(newestDate)}`,
    };
  }, [data]);

  // Get unique product names for dropdown display with react-select format
  const productOptions = useMemo(() => {
    const productMap = {};
    data.forEach((item) => {
      const productCode = item.product_code;
      const productName = item.product_name;
      if (productCode && productName && !productMap[productCode]) {
        productMap[productCode] = productName;
      }
    });

    // Create options in react-select format {value: "", label: ""}
    const options = Object.entries(productMap).map(([code, name]) => ({
      value: code,
      label: `${name} (${code})`,
    }));

    return options;
  }, [data]);

  // Get unique treatment centers for dropdown
  const treatmentCenterOptions = useMemo(() => {
    const tcMap = {};
    data.forEach((item) => {
      const tcId = item.s_register?.tc_id?._id;
      const tcName = item.tc_name;
      if (tcId && tcName && !tcMap[tcId]) {
        tcMap[tcId] = tcName;
      }
    });

    // Create options in react-select format
    const options = Object.entries(tcMap).map(([id, name]) => ({
      value: id,
      label: name,
    }));

    // Add "All Treatment Centers" option
    return [{ value: "all", label: "All Treatment Centers" }, ...options];
  }, [data]);

  // Filter data based on selected products and treatment center
  const filteredData = useMemo(() => {
    let filtered = processedData;

    // Filter by products if any selected
    if (selectedProducts.length > 0) {
      const selectedProductCodes = selectedProducts.map((p) => p.value);
      filtered = filtered.filter((item) =>
        selectedProductCodes.includes(item.product_code)
      );
    }

    // Filter by treatment center if selected
    if (selectedTreatmentCenter !== "all") {
      filtered = filtered.filter(
        (item) => item.tc_id === selectedTreatmentCenter
      );
    }

    return filtered;
  }, [processedData, selectedProducts, selectedTreatmentCenter]);

  // Filter chart data based on selected products and treatment center
  const filteredChartData = useMemo(() => {
    // First filter the processed data by treatment center if needed
    let filteredProcessedData = processedData;
    if (selectedTreatmentCenter !== "all") {
      filteredProcessedData = processedData.filter(
        (item) => item.tc_id === selectedTreatmentCenter
      );
    }

    // Recalculate chart data based on filtered processed data
    let filteredChart = {};

    filteredProcessedData.forEach((item) => {
      const district = item.district;
      const productCode = item.product_code;
      const doseGiven = item.total_dose_given;

      if (!filteredChart[district]) {
        filteredChart[district] = { district };
      }

      if (!filteredChart[district][productCode]) {
        filteredChart[district][productCode] = 0;
      }

      filteredChart[district][productCode] += doseGiven;
    });

    let result = Object.values(filteredChart);

    // If specific products are selected, only show those products' data
    if (selectedProducts.length > 0) {
      const selectedProductCodes = selectedProducts.map((p) => p.value);
      result = result
        .map((item) => {
          const filteredItem = { district: item.district };
          selectedProductCodes.forEach((productCode) => {
            if (item[productCode] !== undefined) {
              filteredItem[productCode] = item[productCode];
            }
          });
          return filteredItem;
        })
        .filter((item) => Object.keys(item).length > 1); // Filter out items with no data for the selected products
    }

    return result;
  }, [processedData, chartData, selectedProducts, selectedTreatmentCenter]);

  // Handle product filter change for react-select (multi-select)
  const handleProductChange = (selectedOptions) => {
    console.log("Selected products:", selectedOptions);
    setSelectedProducts(selectedOptions || []);
  };

  // Handle treatment center filter change
  const handleTreatmentCenterChange = (selectedOption) => {
    console.log("Selected treatment center:", selectedOption);
    setSelectedTreatmentCenter(selectedOption.value);
  };

  // Export to Excel function
  const exportToExcel = () => {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Prepare data for export
    const exportData = filteredData.map((item) => ({
      District: item.district,
      "Treatment Center": item.tc_name,
      "Product Code": item.product_code,
      "Product Name": item.product_name,
      "Total Quantity (Vials)": item.total_quantity,
      "Total Dose Given": item.total_dose_given,
    }));

    // Create worksheet with data
    const worksheet = XLSX.utils.json_to_sheet(exportData, {
      skipHeader: true,
    });

    // Add title and date period as header rows
    XLSX.utils.sheet_add_aoa(
      worksheet,
      [
        ["Total Dose Consumption District-Wise"],
        [`Data Period: ${dateInterval.display}`],
        [], // Empty row for spacing
      ],
      { origin: "A1" }
    );

    // Add the column headers after the title rows
    const headers = [
      "District",
      "Treatment Center",
      "Product Code",
      "Product Name",
      "Total Quantity (Vials)",
      "Total Dose Given",
    ];
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A4" });

    // Add the data starting from row 5
    const data = exportData.map((item) => [
      item["District"],
      item["Treatment Center"],
      item["Product Code"],
      item["Product Name"],
      item["Total Quantity (Vials)"],
      item["Total Dose Given"],
    ]);
    XLSX.utils.sheet_add_aoa(worksheet, data, { origin: "A5" });

    // Set column widths
    const columnWidths = [15, 25, 15, 25, 20, 20];
    worksheet["!cols"] = columnWidths.map((width) => ({ width }));

    // Apply some styling (merge cells for title)
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }, // Merge cells for title
      { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } }, // Merge cells for date period
    ];

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dose Consumption");

    // Generate timestamp for filename
    const now = new Date();
    const timestamp = now
      .toISOString()
      .replace(/[\-:]/g, "")
      .replace("T", "_")
      .split(".")[0]; // Format: YYYYMMDD_HHMMSS

    // Generate Excel file with timestamp and trigger download
    XLSX.writeFile(workbook, `Dose_Consumption_Report_${timestamp}.xlsx`);
  };

  // Generate random colors for chart bars
  const getRandomColor = () => {
    const colors = [
      "#1890ff",
      "#52c41a",
      "#faad14",
      "#f5222d",
      "#722ed1",
      "#13c2c2",
      "#eb2f96",
      "#fadb14",
      "#a0d911",
      "#fa8c16",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Get colors for selected products
  const getProductColors = () => {
    const colors = [
      "#1890ff",
      "#52c41a",
      "#faad14",
      "#f5222d",
      "#722ed1",
      "#13c2c2",
      "#eb2f96",
      "#fadb14",
      "#a0d911",
      "#fa8c16",
    ];

    const productColors = {};
    selectedProducts.forEach((product, index) => {
      productColors[product.value] = colors[index % colors.length];
    });
    return productColors;
  };

  // Define table columns
  const columns = [
    {
      title: "District",
      dataIndex: "district",
      key: "district",
      sorter: (a, b) => a.district.localeCompare(b.district),
    },
    {
      title: "Treatment Center",
      dataIndex: "tc_name",
      key: "tc_name",
      sorter: (a, b) => a.tc_name.localeCompare(b.tc_name),
    },
    {
      title: "Product Code",
      dataIndex: "product_code",
      key: "product_code",
      sorter: (a, b) => a.product_code.localeCompare(b.product_code),
    },
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      sorter: (a, b) => a.product_name.localeCompare(b.product_name),
    },
    {
      title: "Total Quantity (Vials)",
      dataIndex: "total_quantity",
      key: "total_quantity",
      sorter: (a, b) => a.total_quantity - b.total_quantity,
    },
    {
      title: "Total Dose Given",
      dataIndex: "total_dose_given",
      key: "total_dose_given",
      sorter: (a, b) => a.total_dose_given - b.total_dose_given,
    },
  ];

  // Get product colors for chart
  const productColors = getProductColors();

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3}>Total Dose Consumption District-Wise</Title>
          <Text type="secondary">Data Period: {dateInterval.display}</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={exportToExcel}
            style={{ marginLeft: 16 }}
          >
            Export to Excel
          </Button>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <span>
              <FilterOutlined /> Filter by Products:
            </span>
            <Select
              value={selectedProducts}
              onChange={handleProductChange}
              options={productOptions}
              className="product-select"
              classNamePrefix="product-select"
              placeholder="Select products"
              isMulti={true}
              isClearable={true}
              isSearchable={true}
            />
          </Space>
        </Col>
        <Col xs={24} md={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <span>
              <FilterOutlined /> Filter by Treatment Center:
            </span>
            <Select
              value={treatmentCenterOptions.find(
                (option) => option.value === selectedTreatmentCenter
              )}
              onChange={handleTreatmentCenterChange}
              options={treatmentCenterOptions}
              className="product-select"
              classNamePrefix="product-select"
              placeholder="Select a treatment center"
              isClearable={false}
              isSearchable={true}
            />
          </Space>
        </Col>
      </Row>

      <Card className="styled-card">
        <Text strong>Distribution of Product Consumption by District</Text>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={filteredChartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="district" />
              <YAxis />
              <Tooltip />
              <Legend />
              {selectedProducts.length > 0
                ? selectedProducts.map((product) => (
                    <Bar
                      key={product.value}
                      dataKey={product.value}
                      name={product.label}
                      fill={productColors[product.value]}
                    />
                  ))
                : uniqueProductCodes.map((productCode, index) => (
                    <Bar
                      key={productCode}
                      dataKey={productCode}
                      name={`${productCode}`}
                      fill={getRandomColor()}
                    />
                  ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="styled-card">
        <Text strong>Detailed Consumption Data</Text>
        <Table
          className="styled-table"
          columns={columns}
          dataSource={filteredData.map((item, index) => ({
            ...item,
            key: index,
          }))}
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
        />
      </Card>
    </div>
  );
};

export default TotalDoseConsumptionDistrictWise;
